import React, { useState, useEffect, useCallback, useRef } from 'react';
import PeopleBoard from './PeopleBoard';
import { Dashboard } from './Dashboard';
import { ClassView } from './ClassView';
import { UpcomingClassView } from './UpcomingClassView';
import PersonModal from './PersonModal';
import QRModal from './QRModal';

/* --------------------------------------------------------------------------
   Login Screen — "Sign in with Planning Center" OAuth button
   -------------------------------------------------------------------------- */
function LoginScreen({ error, checking }) {
  const handleLogin = () => {
    window.location.href = '/api/auth/login';
  };

  return (
    <div className="login-screen">
      <div className="login-box">
        <h1 className="login-title">People Board Companion</h1>
        <p className="login-subtitle">Sign in with your Planning Center account</p>
        {error && <div className="login-error">{error}</div>}
        <button
          className="login-btn pco-login-btn"
          onClick={handleLogin}
          disabled={checking}
        >
          {checking ? 'Checking session...' : 'Sign in with Planning Center'}
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   URL helpers — /jan2026, /feb2025, etc.
   -------------------------------------------------------------------------- */
const MONTH_ABBRS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];

function parseMonthUrl(path) {
  const clean = path.replace(/^\//, '').toLowerCase();
  if (!clean) return null;
  const match = clean.match(/^([a-z]{3})(\d{4})$/);
  if (!match) return null;
  const monthIdx = MONTH_ABBRS.indexOf(match[1]);
  if (monthIdx === -1) return null;
  return { year: parseInt(match[2], 10), month: monthIdx + 1 };
}

function buildMonthUrl(year, month) {
  if (!year || !month) return '/';
  return `/${MONTH_ABBRS[month - 1]}${year}`;
}

/* --------------------------------------------------------------------------
   Main App
   -------------------------------------------------------------------------- */
function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginError, setLoginError] = useState(null);

  // Parse initial month from URL if present
  const initialMonth = parseMonthUrl(window.location.pathname);

  const [boardData, setBoardData] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [currentYear, setCurrentYear] = useState(initialMonth?.year ?? null);
  const [currentMonth, setCurrentMonth] = useState(initialMonth?.month ?? null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configOk, setConfigOk] = useState(true);
  const [missingConfig, setMissingConfig] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personDetail, setPersonDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [personTeams, setPersonTeams] = useState(null);
  const [personRegistrations, setPersonRegistrations] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'class' | 'upcoming'
  const [classViewData, setClassViewData] = useState(null);
  const [classViewLoading, setClassViewLoading] = useState(false);
  const [upcomingViewData, setUpcomingViewData] = useState(null);
  const [upcomingViewLoading, setUpcomingViewLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);

  /* Check for OAuth callback errors in the URL */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError) {
      const messages = {
        access_denied: 'Access was denied. Please try again.',
        no_code: 'Authentication failed — no authorization code received.',
        state_mismatch: 'Security check failed. Please try again.',
        token_exchange_failed: 'Could not complete sign-in. Please try again.',
        callback_failed: 'Something went wrong during sign-in. Please try again.',
      };
      setLoginError(messages[authError] || `Authentication error: ${authError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  /* On mount, check if we have a valid session cookie */
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'same-origin' })
      .then(res => res.json())
      .then(data => {
        if (data.authenticated && data.user) {
          setUser(data.user);
        }
        setAuthChecked(true);
        setAuthChecking(false);
      })
      .catch(() => {
        setAuthChecked(true);
        setAuthChecking(false);
      });
  }, []);

  /* Authenticated fetch — cookies sent automatically */
  const authFetch = useCallback((url, options = {}) => {
    if (!user) return Promise.reject(new Error('Not authenticated'));
    return fetch(url, { ...options, credentials: 'same-origin' }).then(res => {
      if (res.status === 401) {
        setUser(null);
        throw new Error('Unauthorized');
      }
      return res;
    });
  }, [user]);

  const handleLogout = () => {
    window.location.href = '/api/auth/logout';
  };

  /* Health check */
  useEffect(() => {
    if (!user) return;
    authFetch('/api/health')
      .then(res => res.json())
      .then(data => {
        setConfigOk(data.configured);
        setMissingConfig(data.missing_config || []);
      })
      .catch(() => {
        setError('Cannot connect to backend.');
        setSummaryLoading(false);
      });
  }, [user, authFetch]);

  /* Sync URL when month changes */
  useEffect(() => {
    if (currentYear && currentMonth) {
      const newPath = buildMonthUrl(currentYear, currentMonth);
      if (window.location.pathname !== newPath) {
        window.history.replaceState({}, '', newPath);
      }
    }
  }, [currentYear, currentMonth]);

  /* Handle browser back/forward */
  useEffect(() => {
    const handlePopState = () => {
      const parsed = parseMonthUrl(window.location.pathname);
      if (parsed) {
        setCurrentYear(parsed.year);
        setCurrentMonth(parsed.month);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /* Phase 1: Fetch summary (month list + counts only, no people) */
  const fetchSummary = useCallback((forceRefresh = false) => {
    if (!user || !configOk) { setSummaryLoading(false); return; }
    setSummaryLoading(true);
    setError(null);
    const url = forceRefresh ? '/api/data?refresh=1' : '/api/data';
    authFetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.months) {
          setAvailableMonths(data.months);
          // Auto-select most recent month only if nothing selected (no URL param)
          if (data.months.length > 0 && currentYear === null) {
            const first = data.months[0];
            setCurrentYear(first.year);
            setCurrentMonth(first.month);
          }
        }
        setSummaryLoading(false);
      })
      .catch(err => { setError(err.message); setSummaryLoading(false); });
  }, [user, configOk, authFetch, currentYear]);

  /* Phase 2: Fetch board data for a specific month */
  const fetchBoard = useCallback((year, month, forceRefresh = false) => {
    if (!user || !configOk || !year || !month) return;
    setBoardLoading(true);
    setError(null);
    const refresh = forceRefresh ? '&refresh=1' : '';
    authFetch(`/api/data?year=${year}&month=${month}${refresh}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setBoardData(data.board);
        // Also update months list if returned (keeps counts fresh)
        if (data.months) setAvailableMonths(data.months);
        setBoardLoading(false);
      })
      .catch(err => { setError(err.message); setBoardLoading(false); });
  }, [user, configOk, authFetch]);

  /* Fetch class view data (all people with table host info) */
  const fetchClassView = useCallback(() => {
    if (!user || !configOk) return;
    setClassViewLoading(true);
    authFetch('/api/data?view=class')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setClassViewData(data.people || []);
        setClassViewLoading(false);
      })
      .catch(err => { setError(err.message); setClassViewLoading(false); });
  }, [user, configOk, authFetch]);

  /* Fetch upcoming class view data */
  const fetchUpcomingView = useCallback(() => {
    if (!user || !configOk) return;
    setUpcomingViewLoading(true);
    authFetch('/api/data?view=upcoming')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setUpcomingViewData(data.people || []);
        setUpcomingViewLoading(false);
      })
      .catch(err => { setError(err.message); setUpcomingViewLoading(false); });
  }, [user, configOk, authFetch]);

  /* Fetch class view data when switching to class view */
  useEffect(() => {
    if (viewMode === 'class' && !classViewData && !classViewLoading) {
      fetchClassView();
    }
  }, [viewMode, classViewData, classViewLoading, fetchClassView]);

  /* Fetch upcoming view data when switching to upcoming view */
  useEffect(() => {
    if (viewMode === 'upcoming' && !upcomingViewData && !upcomingViewLoading) {
      fetchUpcomingView();
    }
  }, [viewMode, upcomingViewData, upcomingViewLoading, fetchUpcomingView]);

  /* Trigger Phase 1 on login */
  useEffect(() => {
    if (user && configOk) fetchSummary();
  }, [user, configOk]); // eslint-disable-line

  /* Trigger Phase 2 when month changes or auth completes */
  useEffect(() => {
    if (currentYear && currentMonth) {
      fetchBoard(currentYear, currentMonth);
    }
  }, [currentYear, currentMonth, fetchBoard]);

  /* Refresh: bust edge cache for both summary and current month */
  const handleRefresh = () => {
    setRefreshing(true);

    if (viewMode === 'class') {
      authFetch('/api/data?view=class&refresh=1')
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          setClassViewData(data.people || []);
          setRefreshing(false);
        })
        .catch(err => { setError(err.message); setRefreshing(false); });
      return;
    }

    if (viewMode === 'upcoming') {
      authFetch('/api/data?view=upcoming&refresh=1')
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          setUpcomingViewData(data.people || []);
          setRefreshing(false);
        })
        .catch(err => { setError(err.message); setRefreshing(false); });
      return;
    }

    const refreshUrl = currentYear && currentMonth
      ? `/api/data?year=${currentYear}&month=${currentMonth}&refresh=1`
      : '/api/data?refresh=1';
    authFetch(refreshUrl)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.months) setAvailableMonths(data.months);
        if (data.board) setBoardData(data.board);
        setRefreshing(false);
      })
      .catch(err => { setError(err.message); setRefreshing(false); });
  };

  /* Person detail */
  const handlePersonClick = (person) => {
    setSelectedPerson(person);
    setDetailLoading(true);
    setPersonDetail(null);
    setPersonTeams(null);
    setPersonRegistrations(null);

    authFetch(`/api/person?id=${person.id}`)
      .then(res => res.json())
      .then(data => { setPersonDetail(data); setDetailLoading(false); })
      .catch(() => { setDetailLoading(false); });

    authFetch(`/api/teams?id=${encodeURIComponent(person.id)}&first_name=${encodeURIComponent(person.first_name)}&last_name=${encodeURIComponent(person.last_name)}`)
      .then(res => res.json())
      .then(data => { setPersonTeams(data.teams || []); if (data.debug) console.warn('Teams debug:', data.debug); })
      .catch((err) => { console.error('Teams fetch error:', err); setPersonTeams([]); });
  };

  /* Fetch registrations on demand (when Registrations tab is clicked) */
  const fetchRegistrations = (personId) => {
    if (personRegistrations !== null) return;
    setPersonRegistrations('loading');
    authFetch(`/api/registrations?id=${encodeURIComponent(personId)}`)
      .then(res => {
        if (!res.ok) {
          return res.json().catch(() => ({})).then(data => {
            console.warn('Registrations API error:', data.error || res.status);
            return { registrations: [] };
          });
        }
        return res.json();
      })
      .then(data => {
        setPersonRegistrations(data.registrations || []);
        if (data.warning) console.warn('Registrations:', data.warning);
      })
      .catch((err) => { console.error('Registrations fetch error:', err); setPersonRegistrations([]); });
  };

  const handleCloseModal = () => {
    setSelectedPerson(null);
    setPersonDetail(null);
    setPersonTeams(null);
    setPersonRegistrations(null);
  };

  const handleNoteAdded = () => {
    if (selectedPerson) {
      authFetch(`/api/person?id=${selectedPerson.id}`)
        .then(res => res.json())
        .then(data => { setPersonDetail(data); })
        .catch(() => {});
    }
  };

  /* Search */
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimerRef.current);
    if (q.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      authFetch(`/api/data?view=search&q=${encodeURIComponent(q.trim())}`)
        .then(res => res.json())
        .then(data => {
          setSearchResults(data.results || []);
          setSearchOpen(true);
        })
        .catch(() => {});
    }, 250);
  };

  const handleSearchSelect = (person) => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(false);
    // Navigate to this person's month on the board
    if (person.board_year && person.board_month) {
      setViewMode('board');
      const newPath = buildMonthUrl(person.board_year, person.board_month);
      window.history.pushState({}, '', newPath);
      setCurrentYear(person.board_year);
      setCurrentMonth(person.board_month);
    }
    // Open their profile modal
    handlePersonClick(person);
  };

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* Month navigation */
  const navigateMonth = (direction) => {
    const idx = availableMonths.findIndex(m => m.year === currentYear && m.month === currentMonth);
    const newIdx = idx - direction; // months are sorted newest-first, so "next" = lower index
    if (newIdx >= 0 && newIdx < availableMonths.length) {
      const m = availableMonths[newIdx];
      window.history.pushState({}, '', buildMonthUrl(m.year, m.month));
      setCurrentYear(m.year);
      setCurrentMonth(m.month);
    }
  };

  const handleMonthSelect = (e) => {
    const [y, m] = e.target.value.split('-').map(Number);
    window.history.pushState({}, '', buildMonthUrl(y, m));
    setCurrentYear(y);
    setCurrentMonth(m);
  };

  /* Still checking session... */
  if (!authChecked) {
    return (
      <div className="login-screen">
        <div className="login-box">
          <h1 className="login-title">People Board Companion</h1>
          <div className="spinner" style={{ margin: '24px auto' }} />
        </div>
      </div>
    );
  }

  /* Not authenticated */
  if (!user) {
    return <LoginScreen error={loginError} checking={authChecking} />;
  }

  /* Authenticated — render board */
  const monthName = boardData?.month_name ||
    (currentYear && currentMonth
      ? new Date(currentYear, currentMonth - 1).toLocaleString('default', { month: 'long' })
      : '');

  const currentIdx = availableMonths.findIndex(m => m.year === currentYear && m.month === currentMonth);
  const canGoNewer = currentIdx > 0;
  const canGoOlder = currentIdx >= 0 && currentIdx < availableMonths.length - 1;

  return (
    <div className="app">
      <header className="header">
        <div className="header-top">
          <div className="header-left">
            <h1>People Board Companion</h1>
            {viewMode === 'board' && monthName && <span className="month-label">{monthName} {currentYear}</span>}
            {viewMode === 'class' && <span className="month-label">Class Tables</span>}
            {viewMode === 'upcoming' && <span className="month-label">Upcoming Classes</span>}
          </div>
          <div className="header-right">
            <div className="search-container" ref={searchRef}>
              <input
                type="text"
                className="search-input"
                placeholder="Search people..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
              />
              {searchOpen && searchResults.length > 0 && (
                <div className="search-dropdown">
                  {searchResults.map(person => (
                    <button
                      key={person.id}
                      className="search-result"
                      onClick={() => handleSearchSelect(person)}
                    >
                      {person.avatar ? (
                        <img className="search-result-avatar" src={person.avatar} alt="" />
                      ) : (
                        <div className="search-result-avatar placeholder">
                          {person.first_name?.[0]}{person.last_name?.[0]}
                        </div>
                      )}
                      <div className="search-result-info">
                        <span className="search-result-name">{person.first_name} {person.last_name}</span>
                        {person.board_month && person.board_year && (
                          <span className="search-result-month">
                            {new Date(person.board_year, person.board_month - 1).toLocaleString('default', { month: 'long' })} {person.board_year}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="view-toggle">
              <button
                className={`view-toggle-btn${viewMode === 'board' ? ' active' : ''}`}
                onClick={() => setViewMode('board')}
              >
                People Board
              </button>
              <button
                className={`view-toggle-btn${viewMode === 'class' ? ' active' : ''}`}
                onClick={() => setViewMode('class')}
              >
                Class Tables
              </button>
              <button
                className={`view-toggle-btn${viewMode === 'upcoming' ? ' active' : ''}`}
                onClick={() => setViewMode('upcoming')}
              >
                Upcoming Classes
              </button>
            </div>
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh data from Planning Center"
            >
              {refreshing ? '↻' : '↻'} {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <div className="user-menu">
              {user.avatar && (
                <img className="user-avatar" src={user.avatar} alt="" />
              )}
              <span className="user-name">{user.first_name || user.name}</span>
              <button className="logout-btn" onClick={handleLogout} title="Sign out">Sign Out</button>
            </div>
          </div>
        </div>
        {viewMode === 'board' && (
          <div className="header-board-controls">
            {boardData && (
              <span className="total-badge">
                {boardData.total_count} {boardData.total_count === 1 ? 'person' : 'people'}
              </span>
            )}
            <div className="month-nav">
              <button onClick={() => navigateMonth(1)} disabled={!canGoNewer} title="Newer month">&#8249;</button>
              {availableMonths.length > 0 && (
                <select value={currentYear && currentMonth ? `${currentYear}-${currentMonth}` : ''} onChange={handleMonthSelect}>
                  {availableMonths.map(m => (
                    <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                      {m.label} ({m.count})
                    </option>
                  ))}
                </select>
              )}
              <button onClick={() => navigateMonth(-1)} disabled={!canGoOlder} title="Older month">&#8250;</button>
            </div>
            {currentYear && currentMonth && (
              <button
                className="refresh-btn"
                onClick={() => setShowQR(true)}
                title="Generate QR code for this month"
              >
                QR Code
              </button>
            )}
          </div>
        )}
      </header>

      {!configOk && (
        <div className="config-warning">
          <strong>Configuration needed.</strong> Add these environment variables in your Vercel dashboard:
          {' '}{missingConfig.map(c => <code key={c}>{c}</code>).reduce((acc, el, i) => [...acc, i > 0 ? ', ' : '', el], [])}
        </div>
      )}

      {viewMode === 'board' && summaryLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading months...</span>
        </div>
      )}

      {viewMode === 'board' && !summaryLoading && boardLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading {monthName} {currentYear}...</span>
        </div>
      )}

      {error && !summaryLoading && !boardLoading && !classViewLoading && !upcomingViewLoading && (
        <div className="error-state">
          <span className="error-icon">⚠</span>
          <span>{error}</span>
          <button className="retry-btn" onClick={() => fetchBoard(currentYear, currentMonth)}>Retry</button>
        </div>
      )}

      {viewMode === 'board' && !summaryLoading && !boardLoading && !error && boardData && (
        <>
          {/* <Dashboard metrics={boardData.metrics} /> */}
          <PeopleBoard data={boardData} onPersonClick={handlePersonClick} />
        </>
      )}

      {viewMode === 'class' && classViewLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading class data...</span>
        </div>
      )}

      {viewMode === 'class' && !classViewLoading && classViewData && (
        <ClassView allPeople={classViewData} onPersonClick={handlePersonClick} />
      )}

      {viewMode === 'upcoming' && upcomingViewLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading upcoming classes...</span>
        </div>
      )}

      {viewMode === 'upcoming' && !upcomingViewLoading && upcomingViewData && (
        <UpcomingClassView allPeople={upcomingViewData} onPersonClick={handlePersonClick} />
      )}

      {selectedPerson && (
        <PersonModal person={selectedPerson} detail={personDetail} teams={personTeams} registrations={personRegistrations} loading={detailLoading} onClose={handleCloseModal} onNoteAdded={handleNoteAdded} onLoadRegistrations={fetchRegistrations} />
      )}

      {showQR && currentYear && currentMonth && (
        <QRModal
          url={`${window.location.origin}${buildMonthUrl(currentYear, currentMonth)}`}
          monthLabel={`${monthName} ${currentYear}`}
          onClose={() => setShowQR(false)}
        />
      )}
    </div>
  );
}

export default App;
